import isFunction from 'lodash/isFunction';
import memoize from 'lodash/memoize';
import noop from 'lodash/noop';
import PropTypes from 'prop-types';
import React from 'react';

const mergeDataObjects = (...dataObjects) => {
  return dataObjects.reduce((allData, nextDataObject) => {
    return Object.assign(allData, isFunction(nextDataObject) ? nextDataObject() : nextDataObject);
  }, {});
};

/*

  AppContext can provide all three bits of context needed in various parts of the app:
  * store
  * router
  * integration

  AppContext can be used to pass the above context values from a Provider ancestor to any
  number of Consumer descendants, without passing those values through every component between them.

  `store` and `router` are self-explanatory, but `integrations` requires additional details.
  `integration` provides an api that Consumers can use to dispatch UI event integration actions with
  contextual data that can be listened for elsewhere in the app, e.g. stats domain.

  Consumers will see the following API in this.context.integration:
  * action()
      Calling it should dispatch an integration action

  * getActionData(...additionalDataObjects)
      Returns a data object to be passed into action(), as specified by one or more Provider
      ancestors. As a convenience, one or more optional data objects can be passed in. Each will
      be applied in turn on top of the derived action data supplied by the Provider, all of which
      will be returned as a single data object

  * addActionData(newData)
      Allows a Consumer to act as a provider by adding data to what's supplied by its Provider,
      which it can then pass down to any descendant Consumers. This function creates a new
      integration api with all three of these functions. The `newData` param can either be a plain
      data object, or a function that returns a plain data object. A `newData` function won't be
      called until a Consumer descendant calls `integration.getActionData()`. This allows Providers
      to supply timely data, e.g. from props. It's vital that `newData` isn't a new object or function
      on every render unless the value has actually changed. Generally components should use the
      AppContextIntegrationData helper component, which handles these caching concerns and calls
      `addActionData` internally, so for the most part components don't need to call `addActionData`
      directly.

  * removeActionData(data)
      Removes a data object or function (and associated context api object) from the memoized cache
      of data objects, which is necessary to prevent memory leaks. This is handled by default by
      the AppContextIntegrationData component, so generally components won't need to call this
      directly.


  Every time a Provider changes the supplied context, all descendant Consumers are notified and
  may rerender, so emoizing integration data is essential. It's one of the reasons we don't
  supply raw actionData objects from the integraton context and instead provide a memoized
  `integration` api object.

  To ensure context objects are memoized, we've provided the `createAppContext` helper below.

  A component is a Provider if it wraps <AppContext.Provider value={...}></AppContext.Provider>
  around one or more children. For all but the top-level Provider, `value` should come from
  `context.integration.addActionData(newData)`.

  A component can also be declared an integration context Provider by wrapping in the helper
  component AppContextIntegrationData, which manages caching data and calling `addActionData`
  and `removeActionData` in optimal ways. This is the preferred method of adding integration
  data.

  A component can be made a Consumer in a few ways:
  1. Class-based components can use `static contextType = AppContext;` within the class definition
  2. Class-based components can also use `MyComponent.contextType = AppContext;` after the class definition
  3. In a render function anything wrapped by `<AppContext.Consumer>` has access to the integration context
  4. All else fails, you can use `<AppContext.Consumer>` to pass `integration` as a prop to child components

  Methods 1 & 2 have the benefit of making `this.context` available to all component methods, which
  is often what you'll want. We've eliminated potential conflicts with components that depend on
  the legacy context by making `store` and `router` available via AppContext.

  `integration.action` is an object that contains available general-use integration actions to be
  used within the UI. Currently it contains `clicked` and `viewed` functions, which dispatch `COMPONENT_CLICKED`
  and `COMPONENT_VIEWED` actions respectively.

 */
export const AppContext = React.createContext({
  store: null,
  router: null,
  integration: {
    action: {
      clicked: noop,
      viewed: noop,
    },
    getActionData: noop,
    addActionData: noop,
    removeActionData: noop,
  },
});

/*

  `createAppContext` returns the top-level App context object that should be
  passed as `value` into `<AppContext.Provider>` as far up the app tree as possible.

 */
export function createAppContext({ store, router, integrationActions = {}, integrationData = {} }) {
  function generateContext(dataStack) {
    const getActionData = (...additionalDataObjects) => {
      return mergeDataObjects(...dataStack, ...additionalDataObjects);
    };

    const addActionData = memoize(
      newData => {
        const newDataStack = [...dataStack, newData];
        return generateContext(newDataStack);
      },
      newData => {
        return isFunction(newData) ? newData : JSON.stringify(newData);
      },
    );

    const removeActionData = data => {
      addActionData.cache.delete(data);
    };

    const integrationApi = {
      action: integrationActions,
      getActionData,
      addActionData,
      removeActionData,
    };

    return {
      store,
      router,
      integration: integrationApi,
    };
  }

  return generateContext([integrationData]);
}

/*

  `AppContextIntegrationData` is a helper component that can be used to easily add
  integration `data` (it's only prop) to app context for all of its children. It manages
  correctly adding data to minimize Consumer re-renders and handles removing data from
  the context cache when no longer needed (i.e. all children are un-mounted). For these
  reasons, this is the preferred method of adding integration data, rather than calling
  `context.integration.addActionData()` directly within `<AppContext.Provider>`.
  `AppContextIntegrationData` correctly addresses data memoization concerns, so passing
  a new data object/function on each render cycle doesn't result in unnecessary renders
  in each Consumer.

 */
export class AppContextIntegrationData extends React.Component {
  static propTypes = {
    children: PropTypes.node,
    data: PropTypes.oneOfType([PropTypes.object, PropTypes.func]).isRequired,
  };

  componentWillUnmount() {
    this.context.integration.removeActionData(this.getData);
  }

  static contextType = AppContext;

  getData = () => {
    const { data } = this.props;
    return isFunction(data) ? data() : data;
  };

  render() {
    return (
      <AppContext.Provider value={this.context.integration.addActionData(this.getData)}>
        {this.props.children}
      </AppContext.Provider>
    );
  }
}
